import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BillingConfirmationDialogData, BillingConfirmationDialogResult, BillingConfirmationItem } from 'src/app/core/interfaces/billingConfirmationDialog';
import { Cargo } from 'src/app/core/interfaces/cargo';
import { TransportCompanyBillingGroup } from 'src/app/core/resources/transport-company-context.utils';
import { FreightListService } from 'src/app/modules/cargo/cargo-list/cargo-list.service';

@Component({
  selector: 'app-billing-confirmation-dialog',
  templateUrl: './billing-confirmation-dialog.component.html',
  styleUrls: ['./billing-confirmation-dialog.component.scss']
})
export class BillingConfirmationDialogComponent {

  constructor(
    public dialogRef: MatDialogRef<BillingConfirmationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: BillingConfirmationDialogData,
    public cargoListService: FreightListService
  ) {
    if (!this.data) {
      this.data = {
        title: 'No es posible continuar',
        cargosToBill: [],
        items: [],
        canContinue: false
      };
    }
    this.data.cargosToBill = this.data.cargosToBill || [];
    this.data.items = this.data.items || [];
    this.data.extraMessages = this.data.extraMessages || [];
    this.data.groups = this.data.groups || [];
    const listGroup = this.data.groups.find(group => group.transportCompanyId === this.data.listTransportCompanyId);
    const defaultGroup = listGroup || this.data.groups[0];
    this.selectedTransportCompanyId = defaultGroup ? defaultGroup.transportCompanyId : null;
  }

  // Grupo de empresa de transporte elegido (solo se factura uno).
  selectedTransportCompanyId: string = null;

  get hasGroups(): boolean {
    return this.data.groups.length > 0;
  }

  get isMultiGroup(): boolean {
    return this.data.groups.length > 1;
  }

  get selectedGroup(): TransportCompanyBillingGroup {
    return this.data.groups.find(group => group.transportCompanyId === this.selectedTransportCompanyId) || null;
  }

  selectGroup(group: TransportCompanyBillingGroup): void {
    if (this.isMultiGroup) this.selectedTransportCompanyId = group.transportCompanyId;
  }

  isListGroup(group: TransportCompanyBillingGroup): boolean {
    return !!this.data.listTransportCompanyId && group.transportCompanyId === this.data.listTransportCompanyId;
  }

  getGroupTotal(group: TransportCompanyBillingGroup): number {
    return this.getCargosTotal(group.cargos);
  }

  getCargosTotal(cargos: Cargo[]): number {
    return this.cargoListService.getTotalValueRateCargos(cargos);
  }

  get confirmGroupLabel(): string {
    const group = this.selectedGroup;
    if (!group) return 'Facturar';
    if (group.preBillRows && group.preBillRows.length) {
      const preBillCount = group.preBillRows.length;
      return `Facturar ${preBillCount} ${preBillCount === 1 ? 'prefactura' : 'prefacturas'} de ${group.name}`;
    }
    const count = group.cargos.length;
    return `Facturar ${count} ${count === 1 ? 'servicio' : 'servicios'} de ${group.name}`;
  }

  get enabledItems(): BillingConfirmationItem[] {
    return this.data.items.filter(item => !item.errorMessage);
  }

  get errorItems(): BillingConfirmationItem[] {
    return this.data.items.filter(item => !!item.errorMessage);
  }

  get subtitle(): string {
    if (this.isMultiGroup) {
      return `Los servicios pertenecen a ${this.data.groups.length} empresas de transporte. Elige cuál vas a facturar.`;
    }
    if (this.data.canContinue) {
      return 'Revisa los servicios habilitados y los que presentan error antes de continuar.';
    }
    return 'Ningún servicio está habilitado para facturar. Revisa los errores e inténtalo de nuevo.';
  }

  getKindLabel(item: BillingConfirmationItem): string {
    return item && item.kind === 'prefactura' ? 'Prefactura' : 'Servicio';
  }

  isSomePreBill(items: BillingConfirmationItem[]): boolean {
    return items && items.length && items.some(item => !!(item && item.kind === 'prefactura'));
  }

  getItemRate(item: BillingConfirmationItem): number {
    if (!item || !item.cargo) {
      return null;
    }
    return this.cargoListService.getTotalValueRateCargo(item.cargo);
  }

  close(confirmed: boolean): void {
    const result: BillingConfirmationDialogResult = { state: confirmed };
    const group = this.selectedGroup;
    if (confirmed && group) {
      result.transportCompanyId = group.transportCompanyId;
      result.cargos = group.cargos;
    }
    this.dialogRef.close(result);
  }
}
